import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  listRecentSupportTickets,
  triageSupportTicket,
  summariseIncident,
  fraudCheckCandidate,
  type SupportTriage,
  type IncidentBrief,
  type FraudCheck,
} from "@/lib/ai-assist.functions";

export const Route = createFileRoute("/_authenticated/admin/ai-desk")({
  head: () => ({
    meta: [
      { title: "AI desk — Admin · The Hospitality Reserve" },
      { name: "description", content: "AI triage for support tickets, incident briefs and candidate fraud signals." },
      { property: "og:title", content: "AI desk — Admin" },
      { property: "og:description", content: "AI triage for support tickets, incident briefs and candidate fraud signals." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AiDeskPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

type Ticket = {
  id: string;
  ticket_code: string;
  subject: string;
  message: string;
  status: string | null;
  shift_id: string | null;
  created_at: string;
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
      <h2 className="font-display text-lg">{title}</h2>
      {children}
    </section>
  );
}

function AiDeskPage() {
  const listTickets = useServerFn(listRecentSupportTickets);
  const triage = useServerFn(triageSupportTicket);
  const brief = useServerFn(summariseIncident);
  const fraud = useServerFn(fraudCheckCandidate);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [triaged, setTriaged] = useState<Record<string, SupportTriage>>({});
  const [busyTicket, setBusyTicket] = useState<string | null>(null);

  const [shiftId, setShiftId] = useState("");
  const [incidentBrief, setIncidentBrief] = useState<IncidentBrief | null>(null);
  const [briefBusy, setBriefBusy] = useState(false);

  const [profileId, setProfileId] = useState("");
  const [fraudResult, setFraudResult] = useState<FraudCheck | null>(null);
  const [fraudBusy, setFraudBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const res = await listTickets();
        setTickets(res.tickets as Ticket[]);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not load tickets.");
      }
    })();
  }, []);

  const runTriage = async (id: string) => {
    setBusyTicket(id);
    try {
      const res = await triage({ data: { ticketId: id } });
      setTriaged((prev) => ({ ...prev, [id]: res.triage }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Triage failed.");
    } finally {
      setBusyTicket(null);
    }
  };

  const runBrief = async () => {
    setBriefBusy(true);
    setIncidentBrief(null);
    try {
      const res = await brief({ data: { shiftId: shiftId.trim() } });
      setIncidentBrief(res.brief);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not build the brief.");
    } finally {
      setBriefBusy(false);
    }
  };

  const runFraud = async () => {
    setFraudBusy(true);
    setFraudResult(null);
    try {
      const res = await fraud({ data: { profileId: profileId.trim() } });
      setFraudResult(res.check);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not run the check.");
    } finally {
      setFraudBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-5 py-10 space-y-6">
        <header className="space-y-2">
          <p className="text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground flex items-center gap-2">
            <Sparkles className="w-3 h-3 text-accent" /> Admin · AI desk
          </p>
          <h1 className="font-display text-3xl">AI desk</h1>
          <p className="text-sm text-muted-foreground">
            Triage support tickets, brief yourself on incidents and screen candidates for fraud signals. Every
            suggestion is advisory — you still make the call.
          </p>
        </header>

        <Card title="Support triage">
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tickets yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {tickets.map((t) => {
                const r = triaged[t.id];
                return (
                  <li key={t.id} className="py-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {t.ticket_code} · {t.subject}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(t.created_at).toLocaleString("en-GB")} · {t.status ?? "new"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-xl"
                        disabled={busyTicket === t.id}
                        onClick={() => runTriage(t.id)}
                      >
                        {busyTicket === t.id ? "Triaging…" : "AI triage"}
                      </Button>
                    </div>
                    {r && (
                      <div className="rounded-xl bg-muted/40 p-3 space-y-2 text-sm">
                        <p className="text-xs uppercase tracking-wide-luxury text-muted-foreground">
                          {r.category} · {r.urgency}
                        </p>
                        <p>{r.summary}</p>
                        {r.next_actions.length > 0 && (
                          <ul className="list-disc pl-5 text-xs text-muted-foreground">
                            {r.next_actions.map((a) => (
                              <li key={a}>{a}</li>
                            ))}
                          </ul>
                        )}
                        <div>
                          <p className="text-xs uppercase tracking-wide-luxury text-muted-foreground">
                            Suggested reply
                          </p>
                          <p className="whitespace-pre-wrap text-sm">{r.suggested_reply}</p>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mt-1 h-7 px-2 text-xs"
                            onClick={() => {
                              void navigator.clipboard.writeText(r.suggested_reply);
                              toast.success("Reply copied.");
                            }}
                          >
                            Copy reply
                          </Button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Incident brief">
          <div className="flex gap-2">
            <Input
              value={shiftId}
              onChange={(e) => setShiftId(e.target.value)}
              placeholder="Shift ID"
              className="h-11 rounded-xl"
            />
            <Button className="h-11 rounded-xl" disabled={briefBusy || shiftId.trim().length < 10} onClick={runBrief}>
              {briefBusy ? "Building…" : "Brief me"}
            </Button>
          </div>
          {incidentBrief && (
            <div className="space-y-2 text-sm">
              <p className="text-xs uppercase tracking-wide-luxury text-muted-foreground">
                Risk: {incidentBrief.risk_level}
              </p>
              <p>{incidentBrief.summary}</p>
              {incidentBrief.timeline.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-muted-foreground">
                  {incidentBrief.timeline.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              )}
              <p className="text-foreground/90">{incidentBrief.balanced_view}</p>
              <p className="font-medium">Recommended: {incidentBrief.recommended_action}</p>
              {incidentBrief.evidence_gaps.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Missing evidence: {incidentBrief.evidence_gaps.join(", ")}
                </p>
              )}
            </div>
          )}
        </Card>

        <Card title="Candidate fraud screen">
          <div className="flex gap-2">
            <Input
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              placeholder="Candidate profile ID"
              className="h-11 rounded-xl"
            />
            <Button className="h-11 rounded-xl" disabled={fraudBusy || profileId.trim().length < 10} onClick={runFraud}>
              {fraudBusy ? "Screening…" : "Screen"}
            </Button>
          </div>
          {fraudResult && (
            <div className="space-y-2 text-sm">
              <p className="text-xs uppercase tracking-wide-luxury text-muted-foreground">
                Risk {fraudResult.risk_score}/100 · {fraudResult.risk_level}
              </p>
              {fraudResult.signals.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-muted-foreground">
                  {fraudResult.signals.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              )}
              {fraudResult.duplicate_of && (
                <p className="text-xs text-destructive">Possible duplicate of profile {fraudResult.duplicate_of}</p>
              )}
              <p className="font-medium">{fraudResult.recommendation}</p>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
